// Os refinamentos da listagem/kanban de chamados, guardados entre visitas.
//
// ## Duas durações, de propósito
//
// - **O que o operador ESCOLHEU** -- busca por texto, busca avançada e atalho
//   de indicador -- vai para o `localStorage`. O pedido é que fique "salvo,
//   inclusive ao sair e voltar para o Kanban, **até o próprio usuário mudar ou
//   remover**" (Gisele, ponto 12 da lista de 27/10/2026). Era `sessionStorage`,
//   que o navegador apaga ao fechar: sair e voltar no mesmo dia funcionava,
//   fechar o navegador não. Não há validade por tempo aqui de propósito --
//   "até o usuário mudar ou remover" foi o que ela pediu, e a tela mostra o que
//   está valendo (o termo no campo de busca, os chips da busca avançada).
//
// - **ONDE ele estava na página** (`scrollTop`) continua no `sessionStorage`.
//   Devolver a rolagem ao voltar de um chamado é útil; devolvê-la dias depois,
//   num quadro cujo conteúdo já mudou, abriria a tela no meio do nada.
//
// O Kanban selecionado já se guardava assim antes disto: o reducer de filtros
// grava `selectedFilter` no `localStorage` a cada escolha.
//
// Limpar (o X da barra de busca) apaga os dois.

/** Onde ficam os refinamentos que sobrevivem a fechar o navegador. */
const CHAVE_GUARDADA = 'alodesk:chamados:refinamentos';

/** Onde fica o que só vale enquanto a aba estiver aberta. */
const CHAVE_DA_SESSAO = 'alodesk:chamados:rolagem';

const CAMPOS_GUARDADOS = ['searchValue', 'advancedSearchFilters', 'indicatorShortcut'];
const CAMPOS_DA_SESSAO = ['scrollTop'];

/*
 * Todo acesso é embrulhado: em aba anônima, com dados do site bloqueados ou
 * com a cota cheia, `getItem`/`setItem` LANÇAM. A tela tem de abrir do mesmo
 * jeito, só sem lembrar de nada.
 */
const ler = (deposito, chave) => {
    try {
        const cru = deposito.getItem(chave);
        return cru ? JSON.parse(cru) : null;
    } catch {
        return null;
    }
};

const gravar = (deposito, chave, valor) => {
    try {
        deposito.setItem(chave, JSON.stringify(valor));
    } catch {
        // sem onde guardar: a tela segue, só não lembra
    }
};

const apagar = (deposito, chave) => {
    try {
        deposito.removeItem(chave);
    } catch {
        // idem
    }
};

/*
 * `campo in parcial` e não um teste de verdade: limpar a busca grava
 * `searchValue: ''`, e `''` é falsy. Com um teste de verdade, apagar o termo
 * não seria gravado e ele voltaria na próxima visita.
 */
const recortar = (parcial, campos) => {
    const saida = {};
    campos.forEach((campo) => {
        if (parcial && campo in parcial) saida[campo] = parcial[campo];
    });
    return saida;
};

/**
 * Tudo o que estava guardado, das duas durações, num objeto só.
 *
 * @returns {object} sempre um objeto -- vazio quando não há nada guardado
 */
export function lerRefinamentosDaListagem() {
    return {
        ...(ler(localStorage, CHAVE_GUARDADA) || {}),
        ...(ler(sessionStorage, CHAVE_DA_SESSAO) || {}),
    };
}

/**
 * Grava os campos presentes em `parcial`, cada um no depósito dele.
 *
 * Campo que não está em `parcial` fica como estava: a tela grava a busca e a
 * rolagem em efeitos separados, e um não pode apagar o outro.
 *
 * @param {object} parcial os campos a gravar
 */
export function gravarRefinamentosDaListagem(parcial) {
    const guardados = recortar(parcial, CAMPOS_GUARDADOS);
    if (Object.keys(guardados).length > 0) {
        gravar(localStorage, CHAVE_GUARDADA, {
            ...(ler(localStorage, CHAVE_GUARDADA) || {}),
            ...guardados,
            savedAt: Date.now(),
        });
    }

    const daSessao = recortar(parcial, CAMPOS_DA_SESSAO);
    if (Object.keys(daSessao).length > 0) {
        gravar(sessionStorage, CHAVE_DA_SESSAO, {
            ...(ler(sessionStorage, CHAVE_DA_SESSAO) || {}),
            ...daSessao,
        });
    }
}

/** Esquece tudo: é o "remover" do pedido. */
export function limparRefinamentosDaListagem() {
    apagar(localStorage, CHAVE_GUARDADA);
    apagar(sessionStorage, CHAVE_DA_SESSAO);
}
